import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getDisclaimerPack, type Jurisdiction } from "./jurisdiction";
import { getProtocol, PROTOCOLS, type BodyArea } from "./protocols/definitions";

/**
 * Structured visual observation for a protocol step photo.
 *
 * IMPORTANT — non-diagnostic by design:
 *  - No pathology names, no prognosis, no medication/dose, no benign/malignant.
 *  - Describe what is visible, compare to previous, flag red-flags → practitioner referral.
 */

const BODY_AREAS = [
  "face","scalp","eyebrows_lashes","neck_decollete","scar_site",
  "hands","abdomen","limb","full_body","custom",
] as const;

const PROTOCOL_GOAL_ENUM = PROTOCOLS.map((p) => p.goal) as [string, ...string[]];

const InputSchema = z.object({
  sessionId: z.string().uuid(),
  stepKey: z.string().min(1).max(64),
  goal: z.enum(PROTOCOL_GOAL_ENUM),
  bodyArea: z.enum(BODY_AREAS),
  imageBase64: z.string().min(100).max(22_000_000),
  referenceObject: z.string().max(64).optional(),
  storePhoto: z.boolean().optional().default(false),
  locale: z.enum(["fr","en","nl"]).default("fr"),
  jurisdiction: z
    .enum(["EU_STRICT","UK","CH_NO","US","CA","AU_NZ","DEFAULT"])
    .default("DEFAULT"),
  userObservations: z
    .object({
      pain: z.number().int().min(0).max(10).optional(),
      redness: z.number().int().min(0).max(5).optional(),
      itch: z.number().int().min(0).max(5).optional(),
      notes: z.string().max(1000).optional(),
      sizeRefMm: z.number().positive().max(500).optional(),
    })
    .partial()
    .optional(),
  captureQuality: z
    .object({
      focus: z.enum(["blurry", "ok", "sharp"]).optional(),
      focusVariance: z.number().min(0).max(100_000).optional(),
      lighting: z.enum(["low", "ok", "good", "harsh"]).optional(),
      brightnessMean: z.number().min(0).max(255).optional(),
      brightnessStd: z.number().min(0).max(255).optional(),
      framing: z.enum(["off", "ok", "good"]).optional(),
      tiltDeg: z.number().min(-180).max(180).optional(),
      alignmentScore: z.number().min(0).max(100).optional(),
      scalePxPerMm: z.number().min(0).max(1000).optional(),
      faceLandmarksVersion: z.string().max(32).optional(),
      coachLevel: z.number().int().min(1).max(4).optional(),
    })
    .partial()
    .optional(),
});

const bodyAreaLabel: Record<"fr"|"en"|"nl", Record<BodyArea, string>> = {
  fr: { face: "visage", scalp: "cuir chevelu", eyebrows_lashes: "sourcils / cils", neck_decollete: "cou / décolleté", scar_site: "site cicatriciel", hands: "mains", abdomen: "abdomen", limb: "membre", full_body: "corps entier", custom: "zone personnalisée" },
  en: { face: "face", scalp: "scalp", eyebrows_lashes: "brows / lashes", neck_decollete: "neck / décolletage", scar_site: "scar site", hands: "hands", abdomen: "abdomen", limb: "limb", full_body: "full body", custom: "custom area" },
  nl: { face: "gezicht", scalp: "hoofdhuid", eyebrows_lashes: "wenkbrauwen / wimpers", neck_decollete: "hals / décolleté", scar_site: "littekenplek", hands: "handen", abdomen: "buik", limb: "ledemaat", full_body: "volledig lichaam", custom: "aangepaste zone" },
};

const localeName = { fr: "français", en: "English", nl: "Nederlands" } as const;

/** Prompt version is stored with the observation for traceability. */
export const OBSERVE_PROMPT_VERSION = "observe-v1.1.0";

let _admin: ReturnType<typeof createClient<any, any, any>> | null = null;
function getAdmin() {
  if (!_admin) _admin = createClient<any, any, any>(process.env.SUPABASE_URL!, process.env.SUPABASE_SERVICE_ROLE_KEY!);
  return _admin;
}

export const observeProtocolStep = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => InputSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const admin = getAdmin() as any;
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("Missing LOVABLE_API_KEY");

    const def = getProtocol(data.goal);
    const pack = getDisclaimerPack(data.jurisdiction as Jurisdiction, data.locale);
    const areaLbl = bodyAreaLabel[data.locale][data.bodyArea];

    // Fetch previous observation on same step / body area for the same session.
    const { data: prevRows } = await admin
      .from("protocol_step_events")
      .select("done_at, body_area, ai_observation, user_observations")
      .eq("user_id", userId)
      .eq("session_id", data.sessionId)
      .order("done_at", { ascending: false })
      .limit(3);
    const previousJson = ((prevRows as any[]) ?? []).map((r) => ({
      doneAt: r.done_at,
      bodyArea: r.body_area,
      ai: r.ai_observation,
      user: r.user_observations,
    }));

    const systemPrompt = [
      `You are a non-diagnostic visual observation assistant for a wellness/coaching app.`,
      `Respond entirely in ${localeName[data.locale]}.`,
      `The user is following the "${data.goal}" specialised protocol. Body area: ${areaLbl}.`,
      def ? `Context: ${def.aiContext[data.locale]}` : "",
      ``,
      `STRICT LEGAL GUARDRAILS — never violate, in any locale:`,
      `- DO NOT name any disease, condition, or diagnosis (e.g. rosacea, melanoma, infection).`,
      `- DO NOT give a prognosis or healing timeline.`,
      `- DO NOT recommend, contraindicate, or dose any drug, prescription, or injectable.`,
      `- DO NOT use the words "benign", "malignant", "normal", "abnormal", "pathological".`,
      `- DO NOT claim cure or treatment efficacy.`,
      `- Allowed: describe what is VISIBLE in neutral terms (redness, swelling, oozing, crusts,`,
      `  scaling, pigmentation change, asymmetry, hair/lash length, scar colour & raised/flat).`,
      `- Allowed: estimate measurements ONLY if a scale reference is visible (coin/ruler/sticker),`,
      `  with an explicit ±20% uncertainty.`,
      `- Allowed: compare with previous observations from the same session.`,
      `- If any red flag is present (fever cue, increasing pain reported, purulent discharge,`,
      `  expanding redness >5 days, blistering, asymmetry change), set requiresPractitionerReview=true`,
      `  and propose a referral within 24–48h. Never replace a practitioner visit.`,
      ``,
      `JURISDICTION (${data.jurisdiction}): ${pack.aiGuidance}`,
      ``,
      `Return ONLY valid JSON, no markdown, matching this exact shape:`,
      `{`,
      `  "summary": "1 neutral sentence describing what is visible (<=160 chars)",`,
      `  "visibleFeatures": [{ "label": "...", "intensity": "none|mild|moderate|marked", "note": "..." }],`,
      `  "measurements": [{ "label": "...", "estimateMm": number, "uncertaintyPct": 20 }] /* only when scale ref present */,`,
      `  "changesVsPrevious": "short comparison string or null if no previous",`,
      `  "redFlags": ["short cue", ...],`,
      `  "nextActions": ["concrete non-medical advice", ...],`,
      `  "requiresPractitionerReview": boolean,`,
      `  "practitionerReviewReason": "string or null",`,
      `  "imageQuality": { "lighting": "low|ok|good", "focus": "blurry|ok|sharp", "framing": "off|ok|good" },`,
      `  "disclaimer": "Locale-appropriate non-medical disclaimer."`,
      `}`,
    ].filter(Boolean).join("\n");

    const userBlocks: Array<{ type: "text"; text: string } | { type: "image_url"; image_url: { url: string } }> = [
      { type: "text", text:
        `Step: ${data.stepKey}. Body area: ${areaLbl}.\n` +
        (data.referenceObject ? `Scale reference present: ${data.referenceObject}.\n` : `No scale reference.\n`) +
        (data.userObservations ? `User observations: ${JSON.stringify(data.userObservations)}.\n` : ``) +
      (data.captureQuality ? `Capture quality: ${JSON.stringify(data.captureQuality)}. If focus="blurry" or lighting="low"/"harsh", explicitly flag it in imageQuality and lower confidence. If scalePxPerMm is missing, DO NOT emit any item in "measurements".\n` : ``) +
        (previousJson.length ? `Previous events: ${JSON.stringify(previousJson)}.\n` : ``) +
        `Respond in ${localeName[data.locale]} as JSON only.`,
      },
      { type: "image_url", image_url: { url: data.imageBase64 } },
    ];

    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userBlocks },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (res.status === 429) throw new Error("rate_limited");
    if (res.status === 402) throw new Error("credits_exhausted");
    if (!res.ok) throw new Error(`ai_error_${res.status}`);
    const json = await res.json();
    let parsed: any = {};
    try { parsed = JSON.parse(json.choices?.[0]?.message?.content ?? "{}"); } catch { parsed = {}; }

    // Optional photo storage (consent-gated by caller).
    let imagePath: string | null = null;
    if (data.storePhoto) {
      const m = /^data:(image\/[a-zA-Z+\-]+);base64,(.+)$/.exec(data.imageBase64);
      if (m) {
        const mime = m[1];
        const ext = mime.split("/")[1].replace("jpeg", "jpg").split("+")[0];
        const bin = atob(m[2]);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const path = `${userId}/protocol/${data.sessionId}/${data.stepKey}-${Date.now()}.${ext}`;
        const { error: upErr } = await admin.storage.from("selfies").upload(path, bytes, { contentType: mime, upsert: false });
        if (!upErr) imagePath = path;
      }
    }

    // Persist as a step event (one event per observation; same step can be observed multiple times).
    const { data: inserted, error: insErr } = await admin
      .from("protocol_step_events")
      .insert({
        session_id: data.sessionId,
        user_id: userId,
        step_key: data.stepKey,
        body_area: data.bodyArea,
        reference_object: data.referenceObject ?? null,
        user_observations: data.userObservations ?? null,
        ai_observation: { ...parsed, _promptVersion: OBSERVE_PROMPT_VERSION, _jurisdiction: data.jurisdiction },
        image_path: imagePath,
        capture_quality: data.captureQuality ?? null,
      })
      .select("id")
      .single();
    if (insErr) throw new Error(insErr.message);

    await admin
      .from("protocol_sessions")
      .update({ current_step: data.stepKey })
      .eq("id", data.sessionId)
      .eq("user_id", userId);

    return { eventId: (inserted as any).id as string, observation: parsed as any };
  });